import type { CSSProperties } from "react";

import {
  isModuleActive,
  TERMINAL_MODULE_STATES,
  type CasePlan,
} from "~/lib/cases";
import { extractPhases, type Workflow } from "~/lib/workflows";

import * as css from "~/components/CasePhaseStrip.css";

export interface CasePhaseStripProps {
  /** The workflow the case is linked to. Phases are read from its definition. */
  workflow: Workflow;
  /** The case's plan. Per-phase render state is derived from
   *  plan.modules — terminal states render as past, active states as
   *  current, modules absent from the plan as future. */
  plan: CasePlan;
}

type PhaseRenderState = "past" | "current" | "next" | "future";

/**
 * Horizontal phase progression strip — port of the prototype's
 * interactive phase timeline at
 * `../mayfirm-prototype/MayFirmOS.html:1366-1378`.
 *
 *   - **past**: pale olive bg, olive text, light border. The
 *     phase is done (terminal state in plan).
 *   - **current**: solid blue bg, white text, 2px solid blue
 *     border. The phase is active in the plan. Multiple phases
 *     can render as current at once when a workflow runs them
 *     in parallel.
 *   - **next**: pale blue bg, blue text, 2px dashed blue border.
 *     The first phase after the latest active one — only painted
 *     when there is exactly one active phase, so the cue stays
 *     unambiguous.
 *   - **future**: page-bg, dim gray text, light border. Far out
 *     (phase not in plan yet, or strip's tail beyond next).
 *
 * Compact uppercase typography mirrors the prototype.
 */
export function CasePhaseStrip({ workflow, plan }: CasePhaseStripProps) {
  const phases = extractPhases(workflow.definition);
  if (phases.length === 0) return null;

  return (
    <ol className={css.strip} aria-label="Case progress by phase">
      {phases.map((phase, i) => {
        const state = renderStateFor(phases, plan, i);
        return (
          <li
            key={phase.module.ID}
            className={css.pill}
            style={pillStyle(state)}
            aria-current={state === "current" ? "step" : undefined}
          >
            {phase.module.Meta.name}
          </li>
        );
      })}
    </ol>
  );
}

function renderStateFor(
  phases: ReturnType<typeof extractPhases>,
  plan: CasePlan,
  i: number,
): PhaseRenderState {
  const name = phases[i]!.module.Meta.name;
  const state = plan.modules[name]?.state;

  if (state !== undefined && state !== "" && TERMINAL_MODULE_STATES.has(state)) {
    return "past";
  }
  if (isModuleActive(state)) return "current";

  // Phase isn't in the plan or sits at "" — show as future, except
  // paint the slot right after a single active phase as "next" so
  // the user gets a visible "what comes next" cue. With multiple
  // active phases, skip the next-pill (the cue is ambiguous).
  const activeIndices: number[] = [];
  for (let j = 0; j < phases.length; j++) {
    if (isModuleActive(plan.modules[phases[j]!.module.Meta.name]?.state)) {
      activeIndices.push(j);
    }
  }
  if (activeIndices.length === 1 && i === activeIndices[0]! + 1) {
    return "next";
  }
  return "future";
}

// Color values are pulled directly from the prototype's CSS vars at
// MayFirmOS.html:11-16:
//
//   --green:        #6B8E23 (olive)        --green-pale: rgba(...,.08)
//   --blue:         #2563EB                --blue-pale:  rgba(...,.06)
//   --dim:          #94A3B8                --bg:         #F4F6FA
//   --border-light: #EDF2F7
//
// Inlined here rather than introducing new design tokens because
// these are specific to the phase-strip's "you are here" semantics
// and don't generalize across surfaces. If they grow into a broader
// done/in-progress/upcoming palette we can promote to tokens.
const COLOR_PAST = "#6B8E23";
const COLOR_PAST_BG = "rgba(107, 142, 35, 0.08)";
const COLOR_NEXT = "#2563EB";
const COLOR_NEXT_BG = "rgba(37, 99, 235, 0.06)";
const COLOR_FUTURE_TEXT = "#94A3B8";
const COLOR_FUTURE_BG = "#F4F6FA";
const COLOR_FUTURE_BORDER = "#EDF2F7";

function pillStyle(state: PhaseRenderState): CSSProperties {
  switch (state) {
    case "past":
      return {
        background: COLOR_PAST_BG,
        color: COLOR_PAST,
        borderColor: COLOR_FUTURE_BORDER,
        borderWidth: "1px",
        borderStyle: "solid",
      };
    case "current":
      return {
        background: COLOR_NEXT,
        color: "#ffffff",
        borderColor: COLOR_NEXT,
        borderWidth: "2px",
        borderStyle: "solid",
      };
    case "next":
      return {
        background: COLOR_NEXT_BG,
        color: COLOR_NEXT,
        borderColor: COLOR_NEXT,
        borderWidth: "2px",
        borderStyle: "dashed",
      };
    case "future":
      return {
        background: COLOR_FUTURE_BG,
        color: COLOR_FUTURE_TEXT,
        borderColor: COLOR_FUTURE_BORDER,
        borderWidth: "1px",
        borderStyle: "solid",
      };
  }
}
